import AgedDebtCriteriaModal from '../../components/modals/AgedDebtCriteriaModal';
import React, { ReactElement, useContext, useState } from 'react';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import ModalContext from '../../components/Context/ModalContext';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import { OverdueAccount } from '../../graphql/entities';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import CustomerStatementModal from '../../components/modals/CustomerStatementModal';

export enum CustomerWorkspaceAction {
  RunGlobalAmendments = 'CUSTOMERS_GLOBAL_AMENDMENTS',
  GenerateAgedDebt = 'CUSTOMERS_AGED_DEBT',
  SalesDocumentsPrintStatements = 'SALES_DOCUMENTS_PRINT_STATEMENTS',
  PrintOverdueAccount = 'PRINT_OVERDUE_ACCOUNT',
}

export const workspaceForPermissions = [
  {
    description: 'Run Global Amendments',
    actionReference: CustomerWorkspaceAction.RunGlobalAmendments,
  },
  {
    description: 'Generate Aged Debt',
    actionReference: CustomerWorkspaceAction.GenerateAgedDebt,
  },
  {
    description: 'Print Statements',
    actionReference: CustomerWorkspaceAction.SalesDocumentsPrintStatements,
  },
  {
    description: 'Print Overdue Account',
    actionReference: CustomerWorkspaceAction.PrintOverdueAccount,
  },
];

const useAgedDebtReportAction = (): [IWorkspaceAction<CustomerWorkspaceAction>, ReactElement] => {
  const [modalOpen, setModalOpen] = useState(false);

  return [
    {
      key: 'generateAgedDebt',
      text: 'Generate Aged Debt',
      onClick: () => setModalOpen(true),
      actionReference: CustomerWorkspaceAction.GenerateAgedDebt,
    },
    <AgedDebtCriteriaModal modalOpen={modalOpen} onClose={() => setModalOpen(false)} />,
  ];
};

const useCustomerStatementAction = (): [IWorkspaceAction<CustomerWorkspaceAction>, ReactElement] => {
  const [modalOpen, setModalOpen] = useState(false);

  return [
    {
      key: 'generateStatements',
      text: 'Generate Statements',
      onClick: () => setModalOpen(true),
      actionReference: CustomerWorkspaceAction.SalesDocumentsPrintStatements,
    },
    <CustomerStatementModal modalOpen={modalOpen} onClose={() => setModalOpen(false)} />,
  ];
};

export const useCustomerWorkspaceActions: () => [Array<IWorkspaceAction<CustomerWorkspaceAction>>, ReactElement] =
  () => {
    const [overdueAccountAction, overdueAccountModal] = usePrintOverdueAccountAction();
    const [agedDebtReportAction, agedDebtReportModal] = useAgedDebtReportAction();
    const [customerStatementAction, customerStatementModal] = useCustomerStatementAction();

    const customerWorkspaceActions: Array<IWorkspaceAction<CustomerWorkspaceAction>> = [
      {
        description: 'Run Global Amendments',
        url: '/Customers/GlobalAmendments/New',
        permissionLabel: 'Global Amendments',
        actionReference: CustomerWorkspaceAction.RunGlobalAmendments,
      },
      agedDebtReportAction,
      customerStatementAction,
      overdueAccountAction,
    ];

    return [
      customerWorkspaceActions,
      <>
        {overdueAccountModal}
        {agedDebtReportModal}
        {customerStatementModal}
      </>,
    ];
  };

const usePrintOverdueAccountAction: () => [IWorkspaceAction<CustomerWorkspaceAction>, ReactElement] = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const [printModalState, setPrintModalState] = useState({ modalOpen: false });

  const printOverdueAccountAction: IWorkspaceAction<CustomerWorkspaceAction> = {
    actionReference: CustomerWorkspaceAction.PrintOverdueAccount,
    key: 'printOverdueAccount',
    text: 'Print Overdue Account',
    onClick: () => {
      setPrintModalState({
        modalOpen: true,
      });
    },
  };

  const printOverdueAccountModal = (
    <ModalContext.Provider
      value={{
        onClose: () => {
          setPrintModalState({ modalOpen: false });
          connectedForm.reloadData?.(true); //Reload to update 'pickRequired' flag.
        },
      }}
    >
      <DocumentModal2
        key={'overdueAccountDocuments'}
        entity={OverdueAccount}
        useCustomerDefaults={true}
        modalOpen={printModalState.modalOpen}
        title="Print Overdue Account"
        modalWrapper={true}
        multiValue={true}
        type={'customers'}
        disableIndividual={true}
        blockNavigation={true}
      />
    </ModalContext.Provider>
  );

  return [printOverdueAccountAction, printOverdueAccountModal];
};
